import React, { useState, useContext, useEffect, useMemo } from 'react';
import { AuthContext } from '../AuthContext';
import DigitalSignatureWithGDPR from '../digitalsignature.jsx';
import { useNavigate } from 'react-router-dom';
import { format, differenceInCalendarDays, parseISO } from 'date-fns';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/20/solid';

export default function BackToWorkForm() {
  const user = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    staffName: '',
    returnDate: '',
    startDate: '',
    reasonForAbsence: '',
    fitForWork: false,
    selfCert: false,
    doctorsNote: false,
    adjustmentsNeeded: '',
  });

  const [signature, setSignature] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [lateArrival, setLateArrival] = useState(false);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('backToWork'); // 'backToWork' or 'lateForm'
  const [recentSickDays, setRecentSickDays] = useState([]);
  const [loadingRecentSickDays, setLoadingRecentSickDays] = useState(false);

  const sicknessDurationDays = useMemo(() => {
    if (!formData.startDate || !formData.returnDate) return null;
    try {
      const start = parseISO(formData.startDate);
      const end = parseISO(formData.returnDate);
      const days = differenceInCalendarDays(end, start) + 1;
      return Number.isFinite(days) && days > 0 ? days : null;
    } catch {
      return null;
    }
  }, [formData.startDate, formData.returnDate]);

  const showSelfCertSwitch = sicknessDurationDays !== null && sicknessDurationDays <= 7;
  const showDoctorLetterSwitch = sicknessDurationDays !== null && sicknessDurationDays > 7;

  useEffect(() => {
    let cancelled = false;

    const loadRecentSickDays = async () => {
      setLoadingRecentSickDays(true);
      try {
        const response = await fetch('https://api.insightos.co.uk/api/backtowork/recent-sick-days', {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error('Failed to load recent sick days');
        }

        const payload = await response.json();
        if (!cancelled) {
          setRecentSickDays(Array.isArray(payload.sickDays) ? payload.sickDays : []);
        }
      } catch {
        if (!cancelled) {
          setRecentSickDays([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRecentSickDays(false);
        }
      }
    };

    if (activeTab === 'backToWork') {
      loadRecentSickDays();
    }

    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const handleLateArrivalSubmit = async () => {
  
      console.log(user.manager)
  
      setIsLoading(true);
      try {
        await fetch('https://api.insightos.co.uk/api/clockin/addlatearrival', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            Date: format(new Date(), 'yyyy-MM-dd'),
            Reason: reason,
            Manager: user.manager
          }),
        });
        setLateArrival(false);
        setReason('');
      } catch (err) {
        setError('Failed to submit late arrival reason');
      } finally {
        setIsLoading(false);
      }
    };
  
  // Late Arrival Form Component
  const renderLateForm = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-orange-100 to-red-100 px-6 py-4 border-b border-orange-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <ClockIcon className="h-6 w-6 text-orange-500" />
            Late Arrival Form
          </h2>
          <p className="text-sm text-gray-600 mt-1">Provide a reason for your late arrival</p>
        </div>

        {/* Form Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <p className="text-gray-700 text-base leading-relaxed">
              Hi <span className="font-semibold text-gray-900">{user.name}</span>, 
              please provide an explanation for your late arrival.
            </p>
          </div>

          <div className="space-y-6">
            {/* Staff Information */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-sm font-semibold text-blue-900 mb-3">Staff Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Staff Name
                  </label>
                  <p className="text-base font-semibold text-gray-900 bg-white px-3 py-2 rounded-lg border">
                    {user.name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manager
                  </label>
                  <p className="text-base font-semibold text-gray-900 bg-white px-3 py-2 rounded-lg border">
                    {user.manager}
                  </p>
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Late Arrival <span className="text-red-500">*</span>
              </label>
              <textarea
                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:ring-4 focus:ring-orange-100 transition-all duration-200 resize-none text-gray-700 placeholder-gray-400"
                rows="4"
                placeholder="e.g., Traffic delay, public transport issue, medical appointment..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">Be specific but concise</span>
                <span className="text-xs text-gray-400">{reason.length}/500</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                onClick={() => {
                  setReason('');
                  setActiveTab('backToWork');
                }}
                className="w-full sm:flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleLateArrivalSubmit}
                disabled={isLoading || !reason.trim()}
                data-component="backtowork-late-form"
                data-time-saved="10"
                data-feature="submit_late_arrival_post"
                className="w-full sm:flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-red-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-orange-200 shadow-lg"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Submitting...
                  </div>
                ) : (
                  'Submit Explanation'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            This information will be recorded for attendance tracking purposes.
          </p>
        </div>
      </div>
    </div>
  );
  

  console.log(formData);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const onSignatureChange = (sigBlob) => {
    setSignature(sigBlob);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.returnDate || !formData.startDate || !formData.reasonForAbsence || !signature) {
      setErrorMessage('Please fill in all required fields and provide a signature.');
      return;
    }

    try {


      const data = {
        StaffName: user.name,
        ReturnDate: formData.returnDate,
        StartDate: formData.startDate,
        ReasonForAbsence: formData.reasonForAbsence,
        FitForWork: formData.fitForWork,
        Adjustments: formData.adjustmentsNeeded,
        SelfCert: showSelfCertSwitch && formData.selfCert ? 1 : 0,
        DRLetter: showDoctorLetterSwitch && formData.doctorsNote ? 1 : 0,
        Signed: signature, // should be a string (base64) or null
        Manager : user.manager,
      };

      const response = await fetch('https://api.insightos.co.uk/api/backtowork', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });



      if (!response.ok) {
        throw new Error('Failed to submit the form. Please try again.');
      }

      setSuccessMessage('Back to work form submitted successfully!');
      setErrorMessage('');

      if (user.access === 'staff') {
        navigate('/staffdash');
      } else if (user.access === 'admin' || user.access === 'manager') {
        navigate('/dashboard');
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while submitting the form.');
      setSuccessMessage('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">
              Staff Forms Portal
            </h1>
            <p className="text-indigo-100 text-sm sm:text-base lg:text-lg">
              Complete your staff documentation and reports
            </p>
          </div>
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-8">
            <div className="flex rounded-xl bg-white/70 backdrop-blur-sm border border-white/20 shadow-lg p-1">
              <button
                onClick={() => setActiveTab('backToWork')}
                className={`relative px-6 py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === 'backToWork'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-indigo-600 hover:bg-white/50'
                }`}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5" />
                  Back to Work
                </span>
              </button>
              <button
                onClick={() => setActiveTab('lateForm')}
                className={`relative px-6 py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === 'lateForm'
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg transform scale-105'
                    : 'text-gray-600 hover:text-orange-600 hover:bg-white/50'
                }`}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <ClockIcon className="h-5 w-5" />
                  Late Arrival
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-6 sm:px-6 lg:px-8">
        {activeTab === 'lateForm' ? (
          renderLateForm()
        ) : (
          <div className="max-w-2xl mx-auto">

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
                Back to Work Form
              </h2>
              <p className="text-sm text-gray-600 mt-1">Please complete all required fields</p>
            </div>

            {/* Messages */}
            {successMessage && (
              <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-green-800 font-medium">{successMessage}</span>
                </div>
              </div>
            )}
            {errorMessage && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800 font-medium">{errorMessage}</span>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Staff Information */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-3">Staff Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Staff Name
                    </label>
                    <p className="text-base font-semibold text-gray-900 bg-white px-3 py-2 rounded-lg border">
                      {user.name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Manager
                    </label>
                    <p className="text-base font-semibold text-gray-900 bg-white px-3 py-2 rounded-lg border">
                      {user.manager}
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent sickness days in the past year */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">Your recent sick days (last 12 months)</h3>
                {loadingRecentSickDays ? (
                  <p className="text-sm text-amber-700">Loading recent sick days...</p>
                ) : recentSickDays.length === 0 ? (
                  <p className="text-sm text-amber-700">No sickness days recorded in the last year.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {recentSickDays.slice(0, 30).map((day, idx) => (
                      <span key={`${day}-${idx}`} className="text-xs bg-white border border-amber-300 rounded-full px-3 py-1 text-amber-800">
                        {format(new Date(day), 'dd/MM/yyyy')}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Dates Section */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sickness Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Return Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="returnDate"
                    value={formData.returnDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 text-gray-900"
                  />
                </div>
              </div>

              {sicknessDurationDays !== null && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4">
                  <p className="text-sm text-indigo-800 font-medium">
                    Sickness duration: {sicknessDurationDays} day{sicknessDurationDays === 1 ? '' : 's'}
                  </p>
                </div>
              )}

              {/* Reason for Absence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Absence <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="reasonForAbsence"
                  value={formData.reasonForAbsence}
                  onChange={handleChange}
                  required
                  rows="4"
                  placeholder="Please provide details about your absence..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 resize-none text-gray-900 placeholder-gray-400"
                />
              </div>

              {/* Fit for Work Checkbox */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    name="fitForWork"
                    checked={formData.fitForWork}
                    onChange={handleChange}
                    className="h-5 w-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500 focus:ring-2 mt-0.5 mr-3"
                  />
                  <div>
                    <span className="text-base font-medium text-gray-900">I confirm I am fit for work</span>
                    <p className="text-sm text-gray-600 mt-1">
                      By checking this box, you confirm that you are able to perform your normal duties
                    </p>
                  </div>
                </label>
              </div>

              {showSelfCertSwitch && (
                <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="selfCert"
                      checked={formData.selfCert}
                      onChange={handleChange}
                      className="h-5 w-5 text-sky-600 border-2 border-gray-300 rounded focus:ring-sky-500 focus:ring-2 mt-0.5 mr-3"
                    />
                    <div>
                      <span className="text-base font-medium text-gray-900">Self Certification (0 to 1)</span>
                      <p className="text-sm text-gray-600 mt-1">
                        This absence is under 7 days, so self certification is required.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {showDoctorLetterSwitch && (
                <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      name="doctorsNote"
                      checked={!!formData.doctorsNote}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          doctorsNote: e.target.checked,
                        }))
                      }
                      className="h-5 w-5 text-rose-600 border-2 border-gray-300 rounded focus:ring-rose-500 focus:ring-2 mt-0.5 mr-3"
                    />
                    <div>
                      <span className="text-base font-medium text-gray-900">Doctor Letter (0 to 1)</span>
                      <p className="text-sm text-gray-600 mt-1">
                        This absence is over 7 days. Please confirm a doctor letter has been provided.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Adjustments Needed */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adjustments Needed (Optional)
                </label>
                <textarea
                  name="adjustmentsNeeded"
                  value={formData.adjustmentsNeeded}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe any workplace adjustments or accommodations needed..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all duration-200 resize-none text-gray-900 placeholder-gray-400"
                />
              </div>

              {/* Digital Signature */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Digital Signature Required</h3>
                <DigitalSignatureWithGDPR onSignatureChange={onSignatureChange} />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  data-component="backtowork-form"
                  data-time-saved="10"
                  data-feature="submit_return_to_work_post"
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-4 focus:ring-emerald-200 transition-all duration-200 transform hover:scale-[1.02] disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none"
                >
                  Submit Return to Work Form
                </button>
              </div>
            </form>
          </div>

          {/* Footer Note */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              This form will be reviewed by your manager and HR department.
            </p>
          </div>

        
          
        </div>
        )}
      </div>
    </div>
  );
}
